/**
 * /semantic-cache — Semantic Cache dashboard.
 *
 * Authed light-theme page. Shows hit-rate, $ saved, top-N hot prompts,
 * tuning slider for the similarity threshold, manual probe to test a
 * prompt against the cache.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import BrandTitle from '../components/BrandTitle';
import EmptyDataState from '../components/EmptyDataState';
import { toast } from 'sonner';
import {
  Brain, Activity, DollarSign, Database, Search, Trash2,
  ArrowRight, Zap,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const SemanticCachePage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [threshold, setThreshold] = useState(0.85);
  const [probePrompt, setProbePrompt] = useState('What is our refund policy for SaaS subscriptions?');
  const [probeResult, setProbeResult] = useState(null);
  const [storing, setStoring] = useState(false);

  const fetchStats = async () => {
    try {
      const r = await axios.get(`${API}/semantic-cache/stats`, auth());
      setStats(r.data);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(); }, []);

  const probe = async () => {
    if (!probePrompt.trim()) return;
    try {
      const r = await axios.post(`${API}/semantic-cache/check`, { prompt: probePrompt, threshold }, auth());
      setProbeResult(r.data);
      if (r.data.hit) toast.success(`Cache HIT · ${(r.data.similarity * 100).toFixed(1)}% similar`);
      else toast.message(`Cache miss · best match ${(r.data.similarity * 100).toFixed(1)}%`);
      fetchStats();
    } catch (e) {
      toast.error('Probe failed');
    }
  };

  const storeDemo = async () => {
    if (!probePrompt.trim()) return;
    setStoring(true);
    try {
      await axios.post(`${API}/semantic-cache/store`, {
        prompt: probePrompt,
        answer: 'Demo answer: Per our standard policy, SaaS subscriptions are eligible for a full refund within 30 days of purchase. See /pricing#refund.',
        model: 'gpt-5.2-mini',
        cost_saved_estimate_usd: 0.012,
      }, auth());
      toast.success('Stored. Probe again to see the hit.');
      fetchStats();
    } catch (e) {
      toast.error('Store failed');
    }
    setStoring(false);
  };

  const clearCache = async () => {
    if (!window.confirm('Flush all cache entries for this tenant?')) return;
    try {
      await axios.post(`${API}/semantic-cache/clear`, {}, auth());
      toast.success('Cache cleared');
      setProbeResult(null);
      fetchStats();
    } catch (e) {
      toast.error('Clear failed');
    }
  };

  if (loading) return <EmptyDataState title="Semantic Cache" description="Loading cache stats..." />;

  const hitRate = ((stats?.hit_rate || 0) * 100).toFixed(1);
  const saved = (stats?.saved_usd || 0).toFixed(2);
  const total = stats?.total || 0;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 -mt-px">
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="semantic-cache-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold font-['Manrope'] text-white flex items-center gap-2">
              <Brain className="w-7 h-7 text-emerald-400" />
              <BrandTitle>Semantic Cache</BrandTitle>
            </h1>
            <p className="text-slate-200 text-sm mt-1">
              De-dupe repeated intents. Same answer, $0.00 cost. Engine #25.
            </p>
          </div>
          <Badge className="bg-emerald-500/10 text-emerald-300 border-emerald-500/30 text-sm px-3 py-1">
            <Zap className="w-3.5 h-3.5 mr-1" /> {total > 0 ? `${hitRate}% hit-rate` : 'Awaiting first probe'}
          </Badge>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="semantic-cache-kpis">
          {[
            { Icon: Activity, label: 'Total checks', value: total.toLocaleString(), accent: 'emerald' },
            { Icon: Zap, label: 'Cache hits', value: (stats?.hits || 0).toLocaleString(), accent: 'amber' },
            { Icon: DollarSign, label: 'Saved (USD)', value: `$${saved}`, accent: 'emerald' },
            { Icon: Database, label: 'Entries stored', value: (stats?.entry_count || 0).toLocaleString(), accent: 'teal' },
          ].map((k, i) => {
            const cls = {
              emerald: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
              amber: 'border-amber-500/30 bg-amber-500/10 text-amber-300',
              teal: 'border-teal-500/30 bg-teal-500/10 text-teal-300',
            }[k.accent];
            const Ic = k.Icon;
            return (
              <Card key={i} className={`border ${cls}`}>
                <CardContent className="p-4">
                  <Ic className="w-5 h-5 mb-2 opacity-70" />
                  <div className="text-2xl font-extrabold tabular-nums">{k.value}</div>
                  <div className="text-xs uppercase tracking-wider mt-1 opacity-70">{k.label}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Probe + Store panel */}
        <Card className="border border-emerald-500/30">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-3">
              <Search className="w-5 h-5 text-emerald-500" />
              <h2 className="text-lg font-bold text-white">Probe a prompt</h2>
            </div>
            <p className="text-sm text-slate-300 mb-4">
              Paste any prompt — we'll embed it, scan your tenant cache, and tell you the closest match.
            </p>
            <textarea
              data-testid="probe-textarea"
              value={probePrompt}
              onChange={(e) => setProbePrompt(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-white/15 px-3 py-2 text-sm font-mono focus:border-emerald-500 focus:ring focus:ring-emerald-200 focus:outline-none"
              placeholder="Type the prompt you would normally send to the LLM..."
            />
            <div className="flex items-center justify-between mt-4 flex-wrap gap-3">
              <label className="text-sm text-slate-200 flex items-center gap-3">
                Similarity threshold
                <input
                  data-testid="threshold-slider"
                  type="range" min={0.5} max={0.99} step={0.01}
                  value={threshold}
                  onChange={(e) => setThreshold(parseFloat(e.target.value))}
                  className="w-48"
                />
                <span className="font-mono text-emerald-300 tabular-nums w-12">{threshold.toFixed(2)}</span>
              </label>
              <div className="flex gap-2">
                <Button onClick={probe} data-testid="probe-btn" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold">
                  Probe cache <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
                <Button onClick={storeDemo} disabled={storing} variant="outline" className="border-emerald-300 text-emerald-300 hover:bg-emerald-500/10" data-testid="store-demo-btn">
                  + Store demo answer
                </Button>
              </div>
            </div>

            {probeResult && (
              <div className={`mt-5 rounded-xl border p-4 ${probeResult.hit ? 'border-emerald-300 bg-emerald-500/10' : 'border-amber-300 bg-amber-500/10'}`} data-testid="probe-result">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-xs font-bold uppercase tracking-widest ${probeResult.hit ? 'text-emerald-300' : 'text-amber-300'}`}>
                    {probeResult.hit ? '● Cache HIT' : '○ Cache MISS'}
                  </span>
                  <span className="text-xs text-slate-300 font-mono">
                    Similarity {(probeResult.similarity * 100).toFixed(1)}%
                  </span>
                </div>
                {probeResult.hit ? (
                  <>
                    <p className="text-sm text-white whitespace-pre-wrap">{probeResult.cached_answer}</p>
                    <p className="text-xs text-slate-500 mt-2">
                      Stored {new Date(probeResult.cached_at).toLocaleString()} · hit count {probeResult.hits_count}
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-slate-200">
                    No entry above your {(threshold * 100).toFixed(0)}% threshold. Lower the slider, or store the answer to seed the cache.
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top entries */}
        <Card className="border border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Database className="w-5 h-5 text-emerald-500" />
                <h2 className="text-lg font-bold text-white">Top hot intents (most-hit cache entries)</h2>
              </div>
              <Button onClick={clearCache} variant="outline" size="sm" className="border-rose-300 text-rose-300 hover:bg-rose-500/10" data-testid="clear-cache-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Clear cache
              </Button>
            </div>
            {(stats?.top_entries || []).length === 0 ? (
              <p className="text-sm text-slate-500 italic">No cache entries yet. Probe + Store a few prompts to populate this list.</p>
            ) : (
              <table className="w-full text-sm" data-testid="top-entries-table">
                <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/10">
                  <tr>
                    <th className="pb-2">Prompt preview</th>
                    <th className="pb-2 text-right">Hits</th>
                    <th className="pb-2 text-right">Model</th>
                    <th className="pb-2 text-right">$ Saved</th>
                  </tr>
                </thead>
                <tbody>
                  {(stats?.top_entries || []).map((e) => (
                    <tr key={e.entry_id} className="border-b border-slate-100">
                      <td className="py-2 max-w-md truncate">{e.prompt_preview || '—'}</td>
                      <td className="py-2 text-right font-mono tabular-nums">{e.hits_count}</td>
                      <td className="py-2 text-right font-mono text-xs text-slate-500">{e.model || '—'}</td>
                      <td className="py-2 text-right font-mono tabular-nums text-emerald-300">${(e.cost_saved_estimate_usd || 0).toFixed(4)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Wire-up footnote */}
        <p className="text-xs text-slate-500 italic">
          Tip: the LLM Proxy can auto-check & auto-store on every request. Set <code className="bg-slate-900/60 text-slate-100 px-1 py-0.5 rounded">X-Cartie-Cache: semantic</code> in the request headers.
        </p>
      </div>
    </div>
  );
};

export default SemanticCachePage;
